<template>
  <section class="section section-observed branch-site-page">
    <div v-if="!branchName" class="empty-panel">
      <h2>未找到该分会</h2>
      <button type="button" class="ghost-action compact" @click="front.goPath('/structure')">返回组织机构</button>
    </div>

    <template v-else>
      <div class="branch-shell">
        <header class="branch-hero">
          <div class="branch-hero-top">
            <div class="branch-hero-copy">
              <p v-if="!isMainSite" class="branch-kicker">专业分会</p>
              <h1>{{ branchName }}</h1>
            </div>
            <button type="button" class="branch-back" @click="front.goPath('/structure')">返回组织机构</button>
          </div>

          <nav class="branch-section-nav" :aria-label="isMainSite ? '学会栏目' : '分会栏目'">
            <button
              v-for="item in sections"
              :key="item.id"
              type="button"
              :class="{ active: item.id === currentSection.id }"
              @click="goSection(item.id)"
            >
              {{ item.title }}
            </button>
          </nav>
        </header>

        <BranchHomeFuncBanner
          v-if="!loading && currentSection.kind === 'news-home' && !isImportantNewsPage"
          :slides="homeHeroSlides"
        />

        <div class="branch-panel">
          <div v-if="loading" class="muted-copy">正在加载...</div>
          <template v-else>
            <div v-if="isImportantNewsPage" class="important-news">
              <header class="important-news__header">
                <div>
                  <p class="important-news__eyebrow">{{ branchName }}</p>
                  <h2>重要新闻</h2>
                </div>
                <button type="button" class="important-news__back" @click="goSection('home')"><span aria-hidden="true">←</span> 返回首页</button>
              </header>
              <div class="important-news__list">
                <article v-for="item in importantNewsPageItems" :key="item.entryId" class="important-news__item">
                  <a class="important-news__link" :href="homeNewsPath(item)" @click.prevent="openHomeNews(item)">
                    <div class="important-news__cover">
                      <img :src="front.resolvePublicDisplayUrl(item.coverUrl || item.coverImageUrl || item.mediaUrl)" alt="" loading="lazy">
                    </div>
                    <div class="important-news__body">
                      <div class="important-news__meta"><span v-if="item.category">{{ item.category }}</span><time v-if="item.date">{{ item.date }}</time></div>
                      <h3>{{ item.title }}</h3>
                      <p v-if="item.summary" class="important-news__summary">{{ item.summary }}</p>
                      <span class="important-news__read">阅读全文 <span aria-hidden="true">→</span></span>
                    </div>
                  </a>
                </article>
              </div>
              <div v-if="!homeNewsSlides.length" class="important-news__empty">暂无重要新闻</div>
              <div v-else class="important-news__pagination">
                <FrontPagination :page-num="importantNewsPage" :page-size="10" :total="homeNewsSlides.length" @change="page => importantNewsPage = page" />
              </div>
            </div>
            <div v-else-if="currentSection.kind === 'news-home'" class="branch-home-stage">
              <BranchHomeIntro
                :featured-list="homeNewsSlides"
                :resolve-url="front.resolvePublicDisplayUrl"
                @feature="openHomeNews"
                @more="openImportantNewsPage"
              />
            </div>

            <!-- 概况 -->
            <div
              v-else-if="currentSection.kind === 'page'"
              class="rich-body"
            >
              <div v-if="overviewPage && overviewPage.bodyContent" v-html="overviewPage.bodyContent" />
              <p v-else class="empty-hint">{{ isMainSite ? '暂无学会概况' : '暂无分会概况' }}</p>
            </div>

            <!-- 人员 -->
            <div v-else-if="currentSection.kind === 'personnel'">
              <div class="personnel-grid">
                <article v-for="person in councilMembers" :key="person.entryId" class="personnel-card">
                  <span v-if="person.category" class="personnel-role">{{ person.category }}</span>
                  <h3>{{ person.title }}</h3>
                  <div v-if="person.bodyContent" class="rich-body compact" v-html="person.bodyContent" />
                </article>
              </div>
              <p v-if="!councilMembers.length" class="empty-hint">暂无人员信息。</p>
              <FrontPagination
                :page-num="sectionPager.personnel.pageNum"
                :page-size="sectionPager.personnel.pageSize"
                :total="sectionPager.personnel.total"
                @change="page => onSectionPageChange('personnel', page)"
              />
            </div>

            <!-- 工作动态 / 公告 / 科学传播 -->
            <div v-else-if="['news', 'announcements', 'science'].includes(currentSection.kind)">
              <div v-if="currentSection.kind === 'science'" class="filter-chips">
                <button
                  type="button"
                  :class="{ active: scienceCategory === 'all' }"
                  @click="setScienceCategory('all')"
                >
                  全部
                </button>
                <button
                  v-for="cat in scienceCategories"
                  :key="cat"
                  type="button"
                  :class="{ active: scienceCategory === cat }"
                  @click="setScienceCategory(cat)"
                >
                  {{ cat }}
                </button>
              </div>
              <div class="entry-list">
                <article
                  v-for="item in listEntries"
                  :key="item.entryId"
                  class="entry-card"
                  @click="openEntry(item)"
                >
                  <div class="entry-meta">
                    <span v-if="item.category">{{ item.category }}</span>
                    <time>{{ item.date || '待发布' }}</time>
                  </div>
                  <h3>{{ item.title }}</h3>
                  <p>{{ item.summary || '暂无摘要' }}</p>
                  <div v-if="entryAttachments(item).length" class="entry-file" @click.stop>
                    <button
                      v-for="(file, index) in entryAttachments(item)"
                      :key="file.id || file.url || index"
                      type="button"
                      class="text-link"
                      @click="downloadEntryAttachment(item, file)"
                    >
                      {{ attachmentLabel(file, index, entryAttachments(item).length) }}
                    </button>
                  </div>
                  <p v-else-if="currentSection.kind === 'announcements' && String(item.memberOnly) === '1'" class="login-hint">
                    附件需登录后查看
                  </p>
                </article>
                <p v-if="!listEntries.length" class="empty-hint">{{ listEmptyHint }}</p>
              </div>
              <FrontPagination
                :page-num="activeListPager.pageNum"
                :page-size="activeListPager.pageSize"
                :total="activeListPager.total"
                @change="page => onSectionPageChange(currentSection.kind, page)"
              />
            </div>

            <!-- 沿革 -->
            <div v-else-if="currentSection.kind === 'timeline'" class="entry-list">
              <article
                v-for="item in sortedTimeline"
                :key="item.entryId"
                class="entry-card"
                @click="openEntry(item)"
              >
                <div class="entry-meta">
                  <span v-if="item.category">{{ item.category }}</span>
                  <time>{{ item.date || '待发布' }}</time>
                </div>
                <h3>{{ item.title }}</h3>
              </article>
              <p v-if="!sortedTimeline.length" class="empty-hint">暂无历史沿革。</p>
              <FrontPagination
                :page-num="sectionPager.timeline.pageNum"
                :page-size="sectionPager.timeline.pageSize"
                :total="sectionPager.timeline.total"
                @change="page => onSectionPageChange('timeline', page)"
              />
            </div>

            <!-- 相册 -->
            <div v-else-if="currentSection.kind === 'gallery'" class="gallery-grid-wrap">
              <div class="gallery-grid">
                <figure
                  v-for="photo in branchGallery"
                  :key="photo.entryId"
                  class="is-clickable"
                  role="button"
                  tabindex="0"
                  @click="openGalleryPhoto(photo)"
                  @keydown.enter.space.prevent="openGalleryPhoto(photo)"
                >
                  <img
                    v-if="photo.coverUrl"
                    :src="front.resolvePublicDisplayUrl(photo.coverUrl)"
                    :alt="photo.title"
                  />
                  <figcaption>
                    <strong>{{ photo.title }}</strong>
                    <span v-if="photo.date">{{ photo.date }}</span>
                    <p v-if="photo.importantEvent" class="gallery-event-box">
                      <b>重要事件</b>
                      <span>{{ photo.importantEvent }}</span>
                    </p>
                  </figcaption>
                </figure>
              </div>
              <p v-if="!branchGallery.length" class="empty-hint">暂无相册照片。</p>
              <FrontPagination
                :page-num="sectionPager.gallery.pageNum"
                :page-size="sectionPager.gallery.pageSize"
                :total="sectionPager.gallery.total"
                @change="page => onSectionPageChange('gallery', page)"
              />
            </div>

            <!-- 获奖 -->
            <div v-else-if="currentSection.kind === 'awards'" class="awards-list">
              <article v-for="item in branchAwards" :key="item.entryId" class="award-card">
                <span class="award-year">{{ item.category }}</span>
                <div>
                  <h3>{{ awardTitle(item) }}</h3>
                  <p>获奖人：{{ awardWinner(item) }}</p>
                  <div v-if="item.bodyContent" class="rich-body compact" v-html="item.bodyContent" />
                  <div v-if="entryAttachments(item).length" class="award-files">
                    <button
                      v-for="(file, index) in entryAttachments(item)"
                      :key="file.id || file.url || index"
                      type="button"
                      class="text-link"
                      @click="downloadEntryAttachment(item, file)"
                    >
                      {{ attachmentLabel(file, index, entryAttachments(item).length) }}
                    </button>
                  </div>
                </div>
              </article>
              <p v-if="!branchAwards.length" class="empty-hint">暂无获奖成果。</p>
              <FrontPagination
                :page-num="sectionPager.awards.pageNum"
                :page-size="sectionPager.awards.pageSize"
                :total="sectionPager.awards.total"
                @change="page => onSectionPageChange('awards', page)"
              />
            </div>

            <!-- 下载 -->
            <div v-else-if="currentSection.kind === 'downloads'">
              <div class="filter-chips">
                <button
                  v-for="cat in downloadCategories"
                  :key="cat"
                  type="button"
                  :class="{ active: downloadCategory === cat }"
                  @click="setDownloadCategory(cat)"
                >
                  {{ cat }}
                </button>
              </div>
              <div class="download-list">
                <button
                  v-for="file in pagedDownloads"
                  :key="file.id"
                  type="button"
                  class="download-card"
                  :disabled="!file.fileUrl"
                  @click="file.fileUrl && front.downloadSiteFile(file.fileUrl, file.fileName || file.title)"
                >
                  <div>
                    <strong>{{ file.title }}</strong>
                    <small>{{ file.fileName }}{{ file.fileSize ? ` · ${file.fileSize}` : '' }}</small>
                  </div>
                  <span>{{ file.fileUrl ? '下载' : '暂无文件' }}</span>
                </button>
                <p v-if="!pagedDownloads.length" class="empty-hint">该分类暂无下载文件。</p>
              </div>
              <FrontPagination
                :page-num="sectionPager.downloads.pageNum"
                :page-size="sectionPager.downloads.pageSize"
                :total="filteredDownloads.length"
                @change="page => onSectionPageChange('downloads', page)"
              />
            </div>
          </template>
        </div>
      </div>
    </template>
    <CmsEntryDetailPanel v-if="front.selectedCmsEntry" />
  </section>
</template>

<script>
import {
  BRANCH_DOWNLOAD_CATEGORIES,
  BRANCH_SCIENCE_CATEGORIES,
  branchContentPath,
  branchSitePath,
  branchSiteSectionsFor,
  isMainAssociation,
  resolveBranchSectionFor
} from '../../data/branchSite'
import { listPublicAssociations, listPublicCmsEntries, listPublicFiles, unwrapPublicPage, applyClientPageFallback } from '../../api/paleo'
import { sortCmsByHistoryTime, getAttachments } from '../../utils/cmsExtra'
import FrontPagination from '../../components/common/FrontPagination.vue'
import CmsEntryDetailPanel from '../../components/cms/CmsEntryDetailPanel.vue'
import BranchHomeIntro from '../../components/structure/BranchHomeIntro.vue'
import BranchHomeFuncBanner from '../../components/structure/BranchHomeFuncBanner.vue'
import { BRANCH_HOME_HERO_COLUMN } from '../../data/branchHomeHero'
import { matchesScienceTab, SOCIETY_SCIENCE_TABS } from '../../utils/scienceCategories'
import { branches as FALLBACK_BRANCH_NAMES } from '../../data/siteData'

function parseExtra(extraJson) {
  if (!extraJson) return {}
  try {
    return typeof extraJson === 'string' ? JSON.parse(extraJson) : (extraJson || {})
  } catch (error) {
    return {}
  }
}

function createPager(pageSize) {
  return { pageNum: 1, pageSize, total: 0, items: [] }
}

export default {
  name: 'BranchSitePage',
  components: { CmsEntryDetailPanel, FrontPagination, BranchHomeIntro, BranchHomeFuncBanner },
  inject: ['front'],
  data() {
    return {
      downloadCategories: BRANCH_DOWNLOAD_CATEGORIES,
      scienceCategories: BRANCH_SCIENCE_CATEGORIES,
      scienceCategory: 'all',
      downloadCategory: BRANCH_DOWNLOAD_CATEGORIES[0],
      loading: false,
      loadSeq: 0,
      branchName: '',
      pages: [],
      publicFiles: [],
      homeHeroEntries: [],
      importantNewsPage: 1,
      homePortal: {
        overviewItems: [],
        council: [],
        news: [],
        announcements: [],
        timeline: [],
        gallery: [],
        science: [],
        awards: [],
        downloads: []
      },
      sectionPager: {
        home: createPager(20),
        news: createPager(5),
        announcements: createPager(5),
        science: createPager(5),
        timeline: createPager(5),
        gallery: createPager(5),
        awards: createPager(5),
        downloads: createPager(5),
        personnel: createPager(5),
        pages: createPager(50)
      }
    }
  },
  computed: {
    isImportantNewsPage() {
      return this.currentSection.kind === 'news-home' && this.$route.query.view === 'important-news'
    },
    importantNewsPageItems() {
      const start = (this.importantNewsPage - 1) * 10
      return this.homeNewsSlides.slice(start, start + 10)
    },
    branchId() {
      return String((this.$route.params && this.$route.params.branchId) || '')
    },
    isMainSite() {
      return isMainAssociation({ associationId: Number(this.branchId) || this.branchId })
    },
    sections() {
      return branchSiteSectionsFor(this.isMainSite)
    },
    currentSection() {
      const routeSection = (this.$route.meta && this.$route.meta.branchSection)
        || (this.$route.params && this.$route.params.section)
      return resolveBranchSectionFor(routeSection, this.isMainSite)
    },
    homeNews() {
      const items = this.sectionPager.home.items
      const preferred = items.filter(item => {
        const extra = parseExtra(item.extraJson)
        return extra.showOnHomepage || item.category === '重大科研进展' || item.category === '科研进展'
      })
      return preferred.length ? preferred : items
    },
    homeNewsSlides() {
      return (this.homeNews || []).filter(item => item && (item.coverUrl || item.coverImageUrl || item.mediaUrl))
    },
    homeHeroSlides() {
      return (this.homeHeroEntries || [])
        .filter(row => row && (row.coverUrl || row.coverImageUrl || row.mediaUrl))
        .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
        .map(row => ({
          src: this.front.resolvePublicDisplayUrl(row.coverUrl || row.coverImageUrl || row.mediaUrl),
          title: row.title || '',
          entry: row
        }))
    },
    councilMembers() {
      const group = this.currentSection.personnelGroup || (this.isMainSite ? '理事会' : '委员会')
      return this.sectionPager.personnel.items.filter(item => (item.summary || '') === group)
    },
    overviewPage() {
      const code = this.currentSection.pageCode || (this.isMainSite ? 'society_overview' : 'branch_overview')
      return this.pages.find(item =>
        item.columnCode === code
        || item.columnCode === 'society_overview'
        || item.columnCode === 'branch_overview'
      ) || null
    },
    sortedTimeline() {
      return this.sectionPager.timeline.items
    },
    branchGallery() {
      return this.sectionPager.gallery.items
    },
    branchAwards() {
      return this.sectionPager.awards.items
    },
    activeListPager() {
      const kind = this.currentSection.kind
      return this.sectionPager[kind] || createPager(5)
    },
    listEntries() {
      if (this.currentSection.kind === 'news') return this.sectionPager.news.items
      if (this.currentSection.kind === 'announcements') return this.sectionPager.announcements.items
      if (this.currentSection.kind === 'science') return this.sectionPager.science.items
      return []
    },
    listEmptyHint() {
      if (this.currentSection.kind === 'news') return '暂无工作动态。'
      if (this.currentSection.kind === 'announcements') return '暂无通知公告。'
      return '暂无科学传播内容。'
    },
    downloadItems() {
      const fromCms = this.sectionPager.downloads.items.map(item => {
        const extra = parseExtra(item.extraJson)
        return {
          id: item.entryId || item.title,
          title: item.title || item.fileName,
          category: item.category || '其他',
          fileName: extra.fileName || item.summary || item.originalFileName || item.fileName || '',
          fileUrl: item.fileUrl || '',
          fileSize: extra.fileSize || ''
        }
      })
      const fromFiles = (this.publicFiles || []).map(item => {
        const extra = parseExtra(item.extraJson)
        return {
          id: item.fileId || item.title,
          title: item.title || item.fileName,
          category: item.category || item.fileCategory || '其他',
          fileName: extra.fileName || item.summary || item.originalFileName || item.fileName || '',
          fileUrl: item.fileUrl || '',
          fileSize: extra.fileSize || ''
        }
      })
      return [...fromCms, ...fromFiles]
    },
    filteredDownloads() {
      return this.downloadItems.filter(item => item.category === this.downloadCategory || (!item.category && this.downloadCategory === '其他'))
    },
    pagedDownloads() {
      const pager = this.sectionPager.downloads
      return applyClientPageFallback(
        this.filteredDownloads,
        this.filteredDownloads.length,
        pager.pageNum,
        pager.pageSize
      ).rows
    }
  },
  watch: {
    '$route.query.view'() {
      this.importantNewsPage = 1
    },
    '$route.params.branchId': {
      immediate: true,
      handler() {
        this.bootstrap()
      }
    },
    '$route.meta.branchSection'() {
      if (!this.branchName) return
      this.resetActiveSectionPage()
      this.loadActiveSection()
    }
  },
  methods: {
    resetPagers() {
      this.importantNewsPage = 1
      Object.keys(this.sectionPager).forEach((key) => {
        const pager = this.sectionPager[key]
        pager.pageNum = 1
        pager.total = 0
        pager.items = []
      })
      this.pages = []
      this.publicFiles = []
      this.homeHeroEntries = []
      this.homePortal = {
        overviewItems: [],
        council: [],
        news: [],
        announcements: [],
        timeline: [],
        gallery: [],
        science: [],
        awards: [],
        downloads: []
      }
    },
    resetActiveSectionPage() {
      const key = this.pagerKeyForSection(this.currentSection)
      if (key && this.sectionPager[key]) {
        this.sectionPager[key].pageNum = 1
      }
    },
    pagerKeyForSection(section) {
      if (!section) return null
      if (section.kind === 'news-home') return 'home'
      if (section.kind === 'page') return 'pages'
      return section.kind
    },
    async bootstrap() {
      this.loading = true
      this.scienceCategory = 'all'
      this.downloadCategory = BRANCH_DOWNLOAD_CATEGORIES[0]
      this.resetPagers()
      const fromFront = (this.front.bindableAssociations || []).find(
        item => String(item.associationId) === String(this.branchId)
      )
      this.branchName = (fromFront && fromFront.associationName) || this.fallbackBranchName()
      this.loading = false
      if (!this.branchName) return
      await this.resolveBranchName()
      await this.loadActiveSection()
    },
    async resolveBranchName() {
      const fromFront = (this.front.bindableAssociations || []).find(
        item => String(item.associationId) === String(this.branchId)
      )
      if (fromFront) {
        this.branchName = fromFront.associationName
        return
      }
      try {
        const result = await listPublicAssociations()
        const list = result.data || []
        const found = list.find(item => String(item.associationId) === String(this.branchId))
        this.branchName = found ? found.associationName : this.fallbackBranchName()
        if (list.length && !this.front.bindableAssociations.length) {
          this.front.bindableAssociations = list
        }
      } catch (error) {
        this.branchName = this.fallbackBranchName()
      }
    },
    fallbackBranchName() {
      const index = Number(this.branchId) - 1
      if (!Number.isFinite(index) || index < 0) return ''
      return FALLBACK_BRANCH_NAMES[index] || ''
    },
    onSectionPageChange(sectionKey, page) {
      const next = Number(page)
      if (!Number.isFinite(next) || next < 1) return
      if (!this.sectionPager[sectionKey]) return
      if (next === this.sectionPager[sectionKey].pageNum) return
      this.sectionPager[sectionKey].pageNum = next
      if (sectionKey === 'downloads') return
      this.loadActiveSection()
    },
    setScienceCategory(cat) {
      this.scienceCategory = cat
      this.sectionPager.science.pageNum = 1
      this.loadActiveSection()
    },
    setDownloadCategory(cat) {
      this.downloadCategory = cat
      this.sectionPager.downloads.pageNum = 1
      this.loadActiveSection()
    },
    async loadCmsPage(moduleCode, pager, extraParams = {}, seq = this.loadSeq) {
      const result = await listPublicCmsEntries({
        moduleCode,
        associationId: this.branchId,
        pageNum: pager.pageNum,
        pageSize: pager.pageSize,
        ...extraParams
      })
      if (seq !== this.loadSeq) return pager.items
      const { rows, total } = unwrapPublicPage(result, {
        pageNum: pager.pageNum,
        pageSize: pager.pageSize
      })
      pager.items = rows.map(item => this.front.mapCmsEntry(item))
      pager.total = total
      return pager.items
    },
    mapPublicRows(result) {
      return (unwrapPublicPage(result).rows || []).map(item => this.front.mapCmsEntry(item))
    },
    isHomeNewsItem(item) {
      if (!item) return false
      const extra = parseExtra(item.extraJson)
      return !!(extra.showOnHomepage || item.category === '重大科研进展' || item.category === '科研进展')
    },
    listTitle(item, fallback) {
      if (!item) return fallback || ''
      if (item.category && item.title && item.moduleCode === 'personnel') {
        return `${item.category} ${item.title}`.trim()
      }
      return this.awardTitle(item) || item.title || fallback || ''
    },
    async loadBranchHome() {
      const seq = this.loadSeq
      const id = this.branchId
      const group = this.isMainSite ? '理事会' : '委员会'
      const overviewCode = this.isMainSite ? 'society_overview' : 'branch_overview'
      const overviewLabel = this.isMainSite ? '学会概况' : '分会概况'
      const results = await Promise.all([
        listPublicCmsEntries({ moduleCode: 'news', associationId: id, pageNum: 1, pageSize: 20 }),
        listPublicCmsEntries({ moduleCode: 'pages', associationId: id, pageNum: 1, pageSize: 50 }),
        listPublicCmsEntries({ moduleCode: 'personnel', associationId: id, pageNum: 1, pageSize: 20 }),
        listPublicCmsEntries({ moduleCode: 'announcements', associationId: id, pageNum: 1, pageSize: 8 }),
        listPublicCmsEntries({ moduleCode: 'timeline', associationId: id, pageNum: 1, pageSize: 8 }),
        listPublicCmsEntries({ moduleCode: 'gallery', associationId: id, pageNum: 1, pageSize: 8 }),
        listPublicCmsEntries({ moduleCode: 'science', associationId: id, pageNum: 1, pageSize: 8 }),
        listPublicCmsEntries({ moduleCode: 'awards', associationId: id, pageNum: 1, pageSize: 8 }),
        listPublicCmsEntries({ moduleCode: 'downloads', associationId: id, pageNum: 1, pageSize: 8 }),
        listPublicFiles({ associationId: id, pageNum: 1, pageSize: 8 }).catch(() => ({ rows: [], total: 0 })),
        listPublicCmsEntries({
          moduleCode: 'pages',
          columnCode: BRANCH_HOME_HERO_COLUMN,
          associationId: id,
          pageNum: 1,
          pageSize: 50
        }).catch(() => ({ rows: [], total: 0 }))
      ])
      if (seq !== this.loadSeq) return
      const newsAll = this.mapPublicRows(results[0])
      this.sectionPager.home.items = newsAll
      this.sectionPager.home.total = newsAll.length
      const pages = this.mapPublicRows(results[1])
      this.pages = pages
      this.homeHeroEntries = this.mapPublicRows(results[10])
        .filter(item => item.columnCode === BRANCH_HOME_HERO_COLUMN)
      const overview = pages.find(item =>
        item.columnCode === overviewCode
        || item.columnCode === 'society_overview'
        || item.columnCode === 'branch_overview'
      ) || null
      const overviewText = String((overview && overview.bodyContent) || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
      const withTitle = (list, mapFn) => list.map(item => ({
        ...item,
        title: mapFn ? mapFn(item) : this.listTitle(item)
      }))
      const files = (unwrapPublicPage(results[9]).rows || []).map(item => ({
        id: item.fileId || item.title,
        title: item.fileName || item.title,
        date: item.date || ''
      }))
      this.homePortal = {
        overviewItems: overview
          ? [{ title: overviewText || overview.title || overviewLabel, date: overview.date }]
          : [],
        council: withTitle(this.mapPublicRows(results[2]).filter(item => (item.summary || '') === group)),
        news: withTitle(newsAll.filter(item => !this.isHomeNewsItem(item))),
        announcements: withTitle(this.mapPublicRows(results[3])),
        timeline: withTitle(this.mapPublicRows(results[4])),
        gallery: withTitle(sortCmsByHistoryTime(
          this.mapPublicRows(results[5]),
          (this.front.siteConfig && this.front.siteConfig.galleryHistorySort) || 'asc'
        )),
        science: withTitle(this.mapPublicRows(results[6])),
        awards: withTitle(this.mapPublicRows(results[7])),
        downloads: withTitle(this.mapPublicRows(results[8])).concat(files)
      }
    },
    async loadScienceSection() {
      const pager = this.sectionPager.science
      const seq = this.loadSeq
      const tab = this.scienceCategory && this.scienceCategory !== 'all'
        ? SOCIETY_SCIENCE_TABS.find(item => item.label === this.scienceCategory)
        : null
      const result = await listPublicCmsEntries({
        moduleCode: 'science',
        associationId: this.branchId,
        pageNum: pager.pageNum,
        pageSize: pager.pageSize,
        ...(tab ? { columnCode: tab.columnCode } : {})
      })
      if (seq !== this.loadSeq) return
      const page = unwrapPublicPage(result, {
        pageNum: pager.pageNum,
        pageSize: pager.pageSize
      })
      const rows = (page.rows || [])
        .map(item => this.front.mapCmsEntry(item))
        .filter(item => !tab || matchesScienceTab(item, tab))
      pager.items = rows
      pager.total = page.total
    },
    async loadGallerySection() {
      const pager = this.sectionPager.gallery
      const seq = this.loadSeq
      const result = await listPublicCmsEntries({
        moduleCode: 'gallery',
        associationId: this.branchId,
        pageNum: 1,
        pageSize: 200
      })
      if (seq !== this.loadSeq) return
      const rows = sortCmsByHistoryTime(
        (unwrapPublicPage(result).rows || []).map(item => this.front.mapCmsEntry(item)),
        (this.front.siteConfig && this.front.siteConfig.galleryHistorySort) || 'asc'
      )
      const page = applyClientPageFallback(rows, rows.length, pager.pageNum, pager.pageSize)
      pager.items = page.rows
      pager.total = page.total
    },
    async loadActiveSection() {
      if (!this.branchId || !this.branchName) return
      const seq = ++this.loadSeq
      const section = this.currentSection
      const id = this.branchId
      try {
        if (section.kind === 'news-home') {
          await this.loadBranchHome()
        } else if (section.kind === 'page') {
          await this.loadCmsPage('pages', this.sectionPager.pages)
          if (seq === this.loadSeq) this.pages = this.sectionPager.pages.items
        } else if (section.kind === 'personnel') {
          await this.loadCmsPage('personnel', this.sectionPager.personnel, {
            summary: section.personnelGroup || (this.isMainSite ? '理事会' : '委员会')
          }, seq)
        } else if (section.kind === 'news') {
          const params = {}
          if (section.newsCategory) params.category = section.newsCategory
          await this.loadCmsPage('news', this.sectionPager.news, params)
        } else if (section.kind === 'announcements') {
          await this.loadCmsPage('announcements', this.sectionPager.announcements)
        } else if (section.kind === 'science') {
          await this.loadScienceSection()
        } else if (section.kind === 'timeline') {
          await this.loadCmsPage('timeline', this.sectionPager.timeline)
        } else if (section.kind === 'gallery') {
          await this.loadGallerySection()
        } else if (section.kind === 'awards') {
          await this.loadCmsPage('awards', this.sectionPager.awards)
        } else if (section.kind === 'downloads') {
          const pager = this.sectionPager.downloads
          const [cmsResult, filesResult] = await Promise.all([
            listPublicCmsEntries({
              moduleCode: 'downloads',
              associationId: id,
              pageNum: 1,
              pageSize: 200
            }),
            listPublicFiles({
              associationId: id,
              pageNum: 1,
              pageSize: 200
            }).catch(() => ({ rows: [], total: 0 }))
          ])
          if (seq !== this.loadSeq) return
          const cmsPage = unwrapPublicPage(cmsResult)
          const filesPage = unwrapPublicPage(filesResult)
          pager.items = cmsPage.rows.map(item => this.front.mapCmsEntry(item))
          this.publicFiles = filesPage.rows.map(item => ({
            ...item,
            associationId: item.associationId != null ? item.associationId : Number(id),
            scope: 'branch',
            category: item.fileCategory || item.category || '其他',
            title: item.fileName || item.title,
            fileUrl: item.fileUrl
          }))
        }
      } catch (error) {
        if (seq !== this.loadSeq) return
        // 分会数据不能回退到首页 bootstrap：bootstrap 不保证包含当前分会内容。
        if (section.kind === 'science') {
          this.sectionPager.science.items = []
          this.sectionPager.science.total = 0
          return
        }
        // 其他旧模块仍兼容回退到 front 全局 CMS，再按 associationId 过滤
        const all = this.front.dynamicCmsEntries || []
        const pick = (code) => all.filter(item =>
          item.moduleCode === code
          && (String(item.associationId) === String(id) || (item.scope === 'branch' && String(item.associationId) === String(id)))
        )
        const fillPager = (key, items) => {
          const pager = this.sectionPager[key]
          const paged = applyClientPageFallback(items, items.length, pager.pageNum, pager.pageSize)
          pager.items = paged.rows
          pager.total = paged.total
        }
        if (section.kind === 'news-home') {
          fillPager('home', pick('news'))
          const pages = pick('pages')
          this.homeHeroEntries = pages.filter(item => item.columnCode === BRANCH_HOME_HERO_COLUMN)
          const overviewCode = this.isMainSite ? 'society_overview' : 'branch_overview'
          const group = this.isMainSite ? '理事会' : '委员会'
          const overview = pages.find(item => item.columnCode === overviewCode)
          this.homePortal = {
            overviewItems: overview ? [{ title: overview.title }] : [],
            council: pick('personnel').filter(item => (item.summary || '') === group),
            news: pick('news').filter(item => !this.isHomeNewsItem(item)),
            announcements: pick('announcements'),
            timeline: pick('timeline'),
            gallery: pick('gallery'),
            science: pick('science'),
            awards: pick('awards'),
            downloads: pick('downloads').concat(pick('public-files'))
          }
        } else if (section.kind === 'page') {
          this.pages = pick('pages')
        } else if (section.kind === 'personnel') {
          const group = section.personnelGroup || (this.isMainSite ? '理事会' : '委员会')
          fillPager('personnel', pick('personnel').filter(item => (item.summary || '') === group))
        } else if (section.kind === 'news') {
          fillPager('news', pick('news'))
        } else if (section.kind === 'announcements') {
          fillPager('announcements', pick('announcements'))
        } else if (section.kind === 'science') {
          fillPager('science', pick('science'))
        } else if (section.kind === 'timeline') {
          fillPager('timeline', pick('timeline'))
        } else if (section.kind === 'gallery') {
          fillPager('gallery', sortCmsByHistoryTime(
            pick('gallery').map(item => this.front.mapCmsEntry(item)),
            (this.front.siteConfig && this.front.siteConfig.galleryHistorySort) || 'asc'
          ))
        } else if (section.kind === 'awards') {
          fillPager('awards', pick('awards'))
        } else if (section.kind === 'downloads') {
          this.sectionPager.downloads.items = pick('downloads').concat(pick('public-files'))
          this.publicFiles = this.front.publicFiles || []
        }
      }
    },
    goSection(sectionId) {
      this.front.goPath(branchSitePath(this.branchId, sectionId))
    },
    openImportantNewsPage() {
      this.front.goPath(`${branchSitePath(this.branchId, 'home')}?view=important-news`)
    },
    openHomeNews(item) {
      if (item && item.entryId) {
        this.front.goPath(branchContentPath(this.branchId, 'home', item.entryId))
      }
    },
    homeNewsPath(item) {
      return branchContentPath(this.branchId, 'home', item.entryId)
    },
    openHomeAnnouncement(item) {
      if (item && item.entryId) {
        this.front.goPath(branchContentPath(this.branchId, 'announcements', item.entryId))
      }
    },
    onPortalOpen({ section, item }) {
      if (item && item.entryId && ['work', 'announcements', 'history', 'gallery', 'science', 'awards'].includes(section)) {
        this.front.goPath(branchContentPath(this.branchId, section, item.entryId))
        return
      }
      this.goSection(section)
    },
    openEntry(item) {
      if (item.entryId) {
        this.front.goPath(branchContentPath(this.branchId, this.currentSection.id, item.entryId))
      }
    },
    openGalleryPhoto(photo) {
      this.front.selectedCmsEntry = photo
    },
    awardTitle(item) {
      const extra = parseExtra(item.extraJson)
      return extra.awardName || item.title
    },
    awardWinner(item) {
      const extra = parseExtra(item.extraJson)
      return extra.winner || item.summary || ''
    },
    entryAttachments(item) {
      return getAttachments(item || {})
    },
    attachmentLabel(file, index, count) {
      const name = (file && (file.name || this.front.fileNameFromUrl(file.url))) || ''
      if (count > 1) {
        return `附件${index + 1}${name ? `：${name}` : ''}`
      }
      return name || '下载附件'
    },
    downloadEntryAttachment(item, file) {
      const url = file && file.url
      if (!url) {
        return
      }
      this.front.downloadSiteFile(
        url,
        (file && (file.name || this.front.fileNameFromUrl(url)))
          || (item && item.title)
          || '下载附件'
      )
    }
  }
}
</script>

<style scoped>
.branch-site-page {
  width: 100%;
  max-width: min(1760px, calc(100vw - 48px));
  margin-left: auto;
  margin-right: auto;
  padding-top: 0;
  padding-bottom: 28px;
  padding-left: 24px;
  padding-right: 24px;
  box-sizing: border-box;
}

.branch-shell {
  display: grid;
  gap: 0;
  overflow: hidden;
  width: 100%;
  border: 1px solid var(--border);
  border-radius: calc(var(--front-radius, 6px) + 2px);
  background: #fff;
  box-shadow: 0 10px 28px rgba(0, 43, 73, 0.06);
}

.branch-hero {
  padding: 0;
  background:
    linear-gradient(135deg, rgba(0, 43, 73, 0.04), transparent 42%),
    linear-gradient(180deg, #fff 0%, #f7fafc 100%);
  border-bottom: 1px solid var(--border);
}

.branch-hero-top {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 40px 20px;
  border-bottom: 1px solid rgba(0, 43, 73, 0.08);
}

.branch-kicker {
  margin: 0 0 10px;
  color: var(--secondary, #c4a35a);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.14em;
}

.branch-hero-copy h1 {
  margin: 0;
  color: var(--primary);
  font-size: clamp(28px, 3.2vw, 42px);
  line-height: 1.2;
  font-weight: 900;
  letter-spacing: 0.02em;
}

.branch-back {
  flex: 0 0 auto;
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 8px 16px;
  background: #fff;
  color: var(--primary);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
}

.branch-back:hover {
  border-color: var(--primary);
  background: var(--primary);
  color: #fff;
}

.branch-section-nav {
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  padding: 0 20px;
  margin: 0;
  background: #fff;
}

.branch-section-nav button {
  position: relative;
  border: 0;
  border-radius: 0;
  padding: 16px 18px;
  background: transparent;
  color: var(--subtle);
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
}

.branch-section-nav button::after {
  content: '';
  position: absolute;
  left: 18px;
  right: 18px;
  bottom: 0;
  height: 3px;
  background: transparent;
  border-radius: 3px 3px 0 0;
}

.branch-section-nav button.active {
  color: var(--primary);
}

.branch-section-nav button.active::after,
.branch-section-nav button:hover::after {
  background: var(--gold, var(--secondary));
}

.branch-section-nav button:hover {
  color: var(--primary);
}

.branch-panel {
  padding: 28px 40px 40px;
  min-height: 320px;
  background: #fff;
}

.branch-home-stage {
  display: block;
}

.important-news__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding-bottom: 24px;
  border-bottom: 2px solid var(--primary, #002b49);
}

.important-news__eyebrow {
  margin: 0 0 8px;
  color: var(--subtle, #697681);
  font-size: 13px;
  line-height: 1.6;
}

.important-news__header h2 {
  margin: 0;
  color: var(--primary, #002b49);
  font-size: 28px;
  line-height: 1.3;
  letter-spacing: 1px;
}

.important-news__back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 9px 14px;
  border: 1px solid var(--border, #e5e1da);
  border-radius: 5px;
  background: #fff;
  color: var(--primary, #002b49);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}

.important-news__item {
  border-bottom: 1px solid var(--border, #e5e1da);
}

.important-news__link {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  align-items: start;
  gap: 28px;
  padding: 26px 0;
  color: inherit;
  text-decoration: none;
}

.important-news__cover {
  overflow: hidden;
  aspect-ratio: 16 / 9;
  border-radius: 5px;
  background: #f0f3f5;
}

.important-news__cover img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.important-news__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  color: var(--subtle, #697681);
  font-size: 12px;
}

.important-news__meta span {
  color: var(--primary, #002b49);
  font-weight: 600;
}

.important-news__body h3 {
  margin: 0;
  color: var(--primary, #002b49);
  font-size: 20px;
  line-height: 1.55;
  overflow-wrap: anywhere;
}

.important-news__summary {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  margin: 10px 0 0;
  color: var(--subtle, #697681);
  font-size: 14px;
  line-height: 1.8;
}

.important-news__read {
  display: inline-flex;
  gap: 8px;
  margin-top: 14px;
  color: var(--primary, #002b49);
  font-size: 12px;
  font-weight: 600;
}

.important-news__link:hover h3 {
  text-decoration: underline;
  text-underline-offset: 4px;
}

.important-news__back:hover {
  background: #f3f6f8;
}

.important-news__link:focus-visible,
.important-news__back:focus-visible {
  outline: 2px solid var(--primary, #002b49);
  outline-offset: 4px;
}

.important-news__pagination {
  padding-top: 28px;
}

.important-news__empty {
  padding: 64px 16px;
  text-align: center;
  color: var(--subtle, #697681);
}

@media (max-width: 640px) {
  .important-news__header h2 { font-size: 23px; }
  .important-news__link {
    grid-template-columns: 112px minmax(0, 1fr);
    gap: 14px;
    padding: 20px 0;
  }
  .important-news__body h3 { font-size: 16px; }
  .important-news__meta { gap: 6px; margin-bottom: 7px; }
  .important-news__summary { font-size: 13px; }
}

@media (max-width: 400px) {
  .important-news__link { grid-template-columns: minmax(0, 1fr); }
  .important-news__header { gap: 12px; }
}

.entry-list {
  display: grid;
  gap: 14px;
}

.entry-card {
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 20px 22px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.entry-card:hover {
  border-color: var(--gold, var(--secondary));
  box-shadow: 0 8px 20px rgba(0, 43, 73, 0.06);
}

.entry-meta {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  color: var(--subtle);
  font-size: 12px;
}

.entry-meta span {
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--gold-soft, #f5edd8);
  color: var(--primary);
  font-weight: 800;
}

.entry-card h3,
.personnel-card h3,
.timeline-item h3,
.award-card h3 {
  margin: 0 0 8px;
  color: var(--primary);
  font-size: 18px;
  line-height: 1.4;
}

.entry-card p {
  margin: 0;
  color: var(--subtle);
  font-size: 14px;
  line-height: 1.7;
}

.entry-file {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.entry-file a,
.entry-file .text-link,
.download-card {
  color: var(--primary);
  font-weight: 700;
  text-decoration: none;
}

.entry-file .text-link,
.award-files .text-link {
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 3px 10px;
  background: #f7f7f5;
  color: var(--subtle);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.entry-file .text-link:hover,
.award-files .text-link:hover {
  border-color: var(--secondary);
  color: var(--secondary);
}

.award-files {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.login-hint {
  margin-top: 8px;
  color: var(--secondary);
  font-size: 12px;
  font-weight: 700;
}

.personnel-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.personnel-card {
  border: 1px solid var(--border);
  border-top: 3px solid var(--gold, var(--secondary));
  border-radius: 6px;
  padding: 20px;
}

.personnel-role {
  display: inline-block;
  margin-bottom: 8px;
  color: var(--secondary);
  font-size: 12px;
  font-weight: 800;
}

.timeline-list {
  display: grid;
  gap: 22px;
}

.timeline-item {
  display: flex;
  gap: 20px;
  padding-left: 18px;
  border-left: 3px solid var(--primary);
}

.timeline-year {
  flex: 0 0 80px;
  color: var(--primary);
  font-size: 24px;
  font-style: italic;
  font-weight: 900;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.gallery-grid figure {
  margin: 0;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 6px;
}

.gallery-grid figure.is-clickable {
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.gallery-grid figure.is-clickable:hover,
.gallery-grid figure.is-clickable:focus-visible {
  outline: none;
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 29, 54, 0.12);
}

.gallery-grid figure.is-clickable {
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.gallery-grid figure.is-clickable:hover,
.gallery-grid figure.is-clickable:focus-visible {
  outline: none;
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 29, 54, 0.12);
}

.gallery-grid img {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  display: block;
}

.gallery-grid figcaption {
  display: grid;
  gap: 4px;
  padding: 12px 14px;
  color: var(--primary);
  font-size: 14px;
  font-weight: 800;
}

.gallery-grid figcaption span {
  color: var(--subtle);
  font-size: 12px;
  font-weight: 500;
}

.gallery-event-box {
  display: grid;
  gap: 4px;
  margin: 6px 0 0;
  border-left: 3px solid var(--gold);
  border-radius: 4px;
  padding: 8px 10px;
  background: #f8f5ee;
}

.gallery-event-box b {
  color: var(--primary);
  font-size: 12px;
}

.gallery-event-box span {
  display: -webkit-box;
  overflow: hidden;
  color: var(--ink);
  line-height: 1.6;
  white-space: pre-line;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
}

.awards-list {
  display: grid;
  gap: 14px;
}

.award-card {
  display: flex;
  gap: 16px;
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 18px;
}

.award-year {
  flex: 0 0 auto;
  padding: 6px 10px;
  border-radius: 4px;
  background: var(--secondary);
  color: #fff;
  font-size: 12px;
  font-weight: 800;
}

.award-card p {
  margin: 0;
  color: var(--subtle);
  font-size: 14px;
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}

.filter-chips button {
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 8px 14px;
  background: #fff;
  color: var(--subtle);
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
}

.filter-chips button.active {
  border-color: var(--primary);
  background: var(--primary);
  color: #fff;
}

.download-list {
  display: grid;
  gap: 12px;
}

.download-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 16px 18px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  font: inherit;
}

.download-card:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.download-card:hover {
  border-color: var(--primary);
  background: #f7fbff;
}

.download-card strong {
  display: block;
  margin-bottom: 4px;
}

.download-card small {
  color: var(--subtle);
  font-size: 12px;
}

.rich-body {
  color: var(--text);
  line-height: 1.8;
  font-size: 16px;
}

.rich-body.compact {
  font-size: 14px;
  color: var(--subtle);
}

.rich-body >>> img {
  max-width: 100%;
  height: auto;
}

.empty-hint {
  margin: 48px 0 24px;
  color: var(--subtle);
  text-align: center;
  font-size: 15px;
}

.empty-panel {
  padding: 64px 24px;
  border: 1px solid var(--border);
  border-radius: var(--front-radius);
  background: #fff;
  text-align: center;
}

@media (max-width: 900px) {
  .branch-hero-top,
  .branch-panel {
    padding-left: 22px;
    padding-right: 22px;
  }

  .branch-hero-top {
    flex-direction: column;
    align-items: flex-start;
    padding-top: 28px;
  }

  .branch-section-nav {
    padding: 0 8px;
  }

  .branch-section-nav button {
    padding: 14px 12px;
    font-size: 13px;
  }

  .personnel-grid,
  .gallery-grid {
    grid-template-columns: 1fr;
  }
}
</style>
